import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { getSupportIdentity, type SupportIdentity } from 'src/api/userApi';
import { useAuth } from 'src/contexts/AuthContext';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import {
   beacon,
   buildBeaconSessionData,
   getTripIdFromPath,
   installBeaconScript,
   isBeaconSuppressedPath,
   whenPageIdle,
   type BeaconIdentity,
} from 'src/utils/helpScoutBeacon.util';
import { getBeaconSuggestionSlugs, helpArticleLabelKey, helpArticleUrl } from 'src/utils/helpArticles.util';

/**
 * NPT-2083 — the Help Scout Beacon chat widget, for every site whose config has a
 * `helpScoutBeaconId`. Renders nothing itself; the Beacon draws its own button.
 *
 * - Loads lazily, after the page is idle (see whenPageIdle), and never on admin screens.
 * - Signed in: identifies the traveller with the Secure Mode signature from the API, so the
 *   Beacon shows their earlier conversations. Signing out clears it.
 * - Sends session data (site, user id, page, booking id and admin trip panel link on a trip
 *   page) and a page-viewed event on each SPA navigation.
 * - Suggests the help articles for the page (getBeaconSuggestionSlugs) as links, or hands the
 *   suggestions back to Help Scout on a page without a list.
 *
 * Triage copy, business hours and the after-hours phone message are configured in Help Scout.
 */
const HelpScoutBeacon = () => {
   const { t } = useTranslation();
   const { siteConfig } = useSiteConfig();
   const { user } = useAuth();
   const { pathname, search } = useLocation();

   const beaconId = siteConfig?.helpScoutBeaconId?.trim() || '';
   const suppressed = isBeaconSuppressedPath(pathname);
   const activeId = beaconId && !suppressed ? beaconId : '';

   // The Beacon id currently initialised; '' while not loaded. Effects below wait on it.
   const [initializedId, setInitializedId] = useState('');

   useEffect(() => {
      if (!activeId) return;
      const cancel = whenPageIdle(() => {
         installBeaconScript();
         beacon('init', activeId);
         setInitializedId(activeId);
      });
      return () => {
         cancel();
         beacon('destroy');
         setInitializedId('');
      };
   }, [activeId]);

   const userId = (user as { id?: string } | null)?.id || '';
   const { data: identity, isError: identityFailed } = useQuery<SupportIdentity>({
      queryKey: ['supportIdentity', userId],
      queryFn: () => getSupportIdentity().then(res => res.data),
      enabled: !!initializedId && !!userId,
      staleTime: Infinity,
      retry: 1,
   });

   useEffect(() => {
      if (!initializedId) return;
      if (!userId) {
         beacon('logout', { endActiveChat: true });
         return;
      }
      const fallbackUser = user as { emailAddress?: string; firstName?: string; lastName?: string };
      // An API without the endpoint (or a failed call) still identifies the traveller, unsigned.
      const source: SupportIdentity | null = identity
         ? identity
         : identityFailed && fallbackUser.emailAddress
           ? {
                email: fallbackUser.emailAddress,
                name: [fallbackUser.firstName, fallbackUser.lastName].filter(Boolean).join(' '),
                signature: null,
             }
           : null;
      if (!source?.email) return;
      const payload: BeaconIdentity = { email: source.email };
      if (source.name) payload.name = source.name;
      if (source.signature) payload.signature = source.signature;
      beacon('identify', payload);
   }, [initializedId, userId, identity, identityFailed, user]);

   useEffect(() => {
      if (!initializedId) return;
      const url = `${window.location.origin}${pathname}${search}`;
      beacon(
         'session-data',
         buildBeaconSessionData({
            siteName: siteConfig?.displayName,
            userId,
            pageUrl: url,
            tripId: getTripIdFromPath(pathname),
            origin: window.location.origin,
         }),
      );
      beacon('event', { type: 'page-viewed', url, title: document.title });
      // Help Scout's SPA guidance: suggest after each page-viewed. No argument restores its own.
      const slugs = getBeaconSuggestionSlugs(pathname);
      if (slugs) beacon('suggest', slugs.map(slug => ({ text: t(helpArticleLabelKey(slug)), url: helpArticleUrl(slug) })));
      else beacon('suggest');
   }, [initializedId, pathname, search, siteConfig?.displayName, userId, t]);

   return null;
};

export default HelpScoutBeacon;
